import S, { DataSignal } from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import * as Promise from 'bluebird';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { DraftObject } from 'lib/draftSignals';
import { AutoSavingDraftObject, AutoSaver } from 'lib/autoSave';
import { IAjax } from 'lib/Ajax';
import { ValidationWatcher } from 'lib/validationWatcher';
import nonuniqueIndex from 'lib/nonuniqueIndex';

import { AgencyCode, AgencyCodeOptions } from 'data/models/AgencyCode';
import { LineHandlingCompany, LineHandlingCompanyOptions } from 'data/models/LineHandlingCompany';
import { ManagePilotLookups as ServerManagePilotLookups, ManagePilotLookupsOptions } from 'data/models/ManagePilotLookups';
import { Organization } from 'data/models/Organization';
import { Pilot } from 'data/models/Pilot';
import { PilotAgency } from 'data/models/PilotAgency';
import { TugCompany, TugCompanyOptions } from 'data/models/TugCompany';

import { PCCache } from 'data/PCCache';

export interface ManagePilotLookupsParams {
    id : number;
}

export function ManagePilotLookups(
    ajax : IAjax,
    loader : CachedModelLoader<ServerManagePilotLookups, ManagePilotLookupsOptions>,
    data : PCCache,
    { id } : ManagePilotLookupsParams
) {
    const { model } = loader({ Id: id }, 30),
        autoEditAgency = { autoEdit: false },
        autoEditTug = { autoEdit: false },
        autoEditLineHandler = { autoEdit: false },
        agencyCodes = lift(() => model.PilotAgency() ? model.PilotAgency()!.AgencyCodes() : [])
            .map(ac => AgencyCodeViewModel(ac, ajax, model.Organizations, data, AgencyCodesUrl, autoEditAgency)),
        pilots = model.Pilots.map(p => PilotViewModel(p, ajax)),
        lineHandlingCompanies = lift(() => model.PilotAgency() ? model.PilotAgency()!.LineHandlingCompanies() : [])
            .filter(l => l.Active())
            .map(l => LineHandlingCompanyViewModel(l, ajax, autoEditLineHandler)),
        tugCompanies = lift(() => model.PilotAgency() ? model.PilotAgency()!.TugCompanies() : [])
            .filter(t => t.Active())
            .map(t => TugCompanyViewModel(t, ajax, autoEditTug)),
        assignedOrgsArray = agencyCodes
            .filter(c => !!c.Model.CodedOrganization())
            .map(c => c.Model.CodedOrganization()!),
        assignedOrganizations = S(() => new Set(assignedOrgsArray())),
        // All the orgs that are available for any (new or existing) agency code
        allOrgs = lift(() => Array.from(data.Organizations.items()))
            .filter(o => o.Active()),
        globallyAvailableOrgs = allOrgs
            .filter(o => !assignedOrganizations().has(o)),
        addTugClient = ajax.Url<TugCompanyOptions>(() => model.PilotAgency() && model.PilotAgency()!.TugCompaniesUrl() || ''),
        addLineHandlerClient = ajax.Url<LineHandlingCompanyOptions>(() => model.PilotAgency() && model.PilotAgency()!.LineHandlingCompaniesUrl() || '');

    return {
        Model: model,
        AgencyCodes: agencyCodes,
        Pilots: pilots,
        TugCompanies: tugCompanies,
        LineHandlingCompanies: lineHandlingCompanies,
        GloballyAvailableOrganizations: globallyAvailableOrgs,
        EnableAddAgencyCode: () => !!model.PilotAgency() && globallyAvailableOrgs().length > 0,
        AddAgencyCode,
        OrganizationsAvailableForAgencyCode,
        EnableAddTug: () => !!model.PilotAgency(),
        AddTugCompany,
        EnableAddLineHandler: () => !!model.PilotAgency(),
        AddLineHandlingCompany
    };

    function AddAgencyCode() {
        const orgs = globallyAvailableOrgs();
        if (!orgs.length) return;
        const codedOrgId = orgs[0].Id(),
            req = ajax.Url<AgencyCodeOptions>(AgencyCodesUrl(codedOrgId)).Put({
                PilotAgencyId: 0,
                CodedOrganizationId: 0,
                Code: '--'
            });
        req.then(json => {
            autoEditAgency.autoEdit = true;
            data.AgencyCodes.update1(req.Date, json);
        });
    }

    function OrganizationsAvailableForAgencyCode(code : AgencyCodeViewModel) {
        return allOrgs().filter(o => !assignedOrganizations().has(o) || o === code.Model.CodedOrganization());
    }

    function AgencyCodesUrl(codedOrgId : number) {
        return model.PilotAgency()
            && model.PilotAgency()!.AgencyCodesUrlBase()
            && model.PilotAgency()!.AgencyCodesUrlBase()!.replace('{codedOrganizationId}', '' + codedOrgId)
            || '';
    }

    function AddTugCompany() {
        const req = addTugClient.Post({
            Id: 0,
            Active: true,
            Code: '--',
            Name: '--',
        });
        req.then(json => {
            autoEditTug.autoEdit = true;
            data.TugCompanies.update1(req.Date, json);
        });
    }

    function AddLineHandlingCompany() {
        const req = addLineHandlerClient.Post({
            Id: 0,
            Active: true,
            Code: '--',
            Name: '--'
        });
        req.then(json => {
            autoEditLineHandler.autoEdit = true;
            data.LineHandlingCompanies.update1(req.Date, json);
        })
    }
}
export type ManagePilotLookups = ReturnType<typeof ManagePilotLookups>;

function AgencyCodeViewModel(
    agencyCode : AgencyCode,
    ajax : IAjax,
    orgs : ArraySignal<Organization>,
    data : PCCache, 
    agencyCodesUrl : (codedOrgId : number) => string,
    autoEditContainer : { autoEdit : boolean }
) {
    const deleteClient = ajax.Url<AgencyCode>(() => agencyCode.Url() || ''),
        { object: draft, methods: draftMethods } = DraftObject(agencyCode, [
            // TH 01/18/2017 -- Explicitly enumerating properties so we don't
            // draft the CodedOrganization navigation property.
            // Otherwise we can run into scenarios where persisting a change to CodedOrganizationId
            // can trigger a change to CodedOrganization (via the data.AgencyCodes.attach call below),
            // which marks CodedOrganization as dirty and triggers an extra unnecessary save.
            'CodedOrganizationId',
            'Code'
        ]),
        client = ajax.Url<AgencyCode, AgencyCodeOptions>(() => agencyCodesUrl(draft.CodedOrganizationId())),
        editing = S.data(autoEditContainer.autoEdit);

    AutoSaver(draftMethods.onWrite, () => {
        let initialRequest : Promise<any> = Promise.resolve();
        if (draft.CodedOrganizationId() !== agencyCode.CodedOrganizationId()) initialRequest = deleteClient.Delete();
        // TH 01/17/2017 -- If CodedOrganizationId is changing, delete the old AgencyCode then PUT then new one.
        return initialRequest.then(() => {
            const req = client.Put(draft),
                checkpoint = draftMethods.checkpoint();
            return req.then(json => S.freeze(() => {
                // TH 01/17/2017 -- Need to actually update from json instead of just calling
                // draftMethods.commit b/c this request can change the AgencyCode's
                // CodedOrganizationId, thus changing its key and, in particular, its URL.
                data.AgencyCodes.update1(req.Date, json, agencyCode);
                // TH 01/18/2017 -- NOTE: Have to do draftMethods.revert instead of draftMethods.commit.
                // draftMethods.commit creates the possibility that we might be committing draft changes
                // in advance of when they're actually persisted.
                // Since we're also updating the original agencyCode from the persisted json in the line
                // above, committing here could cause S.js conflicting value errors.
                checkpoint.revert();
            }));
        });
    });

    draft.CodedOrganization = S.value(S.sample(agencyCode.CodedOrganization));
    data.AgencyCodes.attach(draft, [draft.CodedOrganization]);

    autoEditContainer.autoEdit = false;

    return {
        Model: draft,
        Editing: editing,
        Delete: Delete,
        Validation: ValidationWatcher(client)
    };

    function Delete() {
        const id = AgencyCode.key(agencyCode),
            req = deleteClient.Delete();
        req.then(() => data.AgencyCodes.update(req.Date, [], [id]));
    }
}
export type AgencyCodeViewModel = ReturnType<typeof AgencyCodeViewModel>;

function PilotViewModel(
    pilot : Pilot,
    ajax : IAjax,
) {
    const client = ajax.Url<Pilot>(() => pilot.Url() || ''),
        draft = AutoSavingDraftObject(pilot, client),
        editing = S.data(false),
        code : DataSignal<string | null> = (val? : string | null) => {
            if (val === undefined) return draft.Code();
            // TH 01/17/2017: Coerce empty string to null.
            // Unlike AgencyCode.Code, LineHandlingCompany.Code, and TugCompany.Code, Pilot.Code
            // is not marked Required server-side.
            // But like the other Code fields, it does have minimum-length validation.
            // The empty string fails length validation, but null passes.
            // So this gives a way of resetting the value of the field to null.
            return draft.Code(val || null);
        };

    return {
        Model: draft,
        Code: code,
        Editing: editing,
        Validation: ValidationWatcher(client)
    };
}
export type PilotViewModel = ReturnType<typeof PilotViewModel>;

function TugCompanyViewModel(
    tug : TugCompany,
    ajax : IAjax,
    autoEditContainer : { autoEdit : boolean }
) {
    const client = ajax.Url<TugCompany>(() => tug.Url() || ''),
        draft = AutoSavingDraftObject(tug, client),
        editing = S.data(autoEditContainer.autoEdit);

    autoEditContainer.autoEdit = false;

    return {
        Model: draft,
        Editing: editing,
        Delete: () => draft.Active(false),
        Validation: ValidationWatcher(client)
    };
}
export type TugCompanyViewModel = ReturnType<typeof TugCompanyViewModel>;

function LineHandlingCompanyViewModel(
    handler : LineHandlingCompany,
    ajax : IAjax,
    autoEditContainer : { autoEdit : boolean}
) {
    const client = ajax.Url<LineHandlingCompany>(() => handler.Url() || ''),
        draft = AutoSavingDraftObject(handler, client),
        editing = S.data(autoEditContainer.autoEdit);

    autoEditContainer.autoEdit = false;

    return {
        Model: draft,
        Editing: editing,
        Delete: () => draft.Active(false),
        Validation: ValidationWatcher(client)
    };
}
export type LineHandlingCompanyViewModel = ReturnType<typeof LineHandlingCompanyViewModel>;


// WEBPACK FOOTER //
// ./apps/pilots/managepilotlookups/model.ts